import createDependableResource from 'utils/zustand/createDependableResource'
import createPeriodicRefresher from 'utils/createPeriodicRefresher'
import BigNumber from 'bignumber.js'
import { getAccount, onAccountInitOrChange } from 'state/web3/account'
import { useFarmTokensPricesUsdc } from 'state/farms/useFarmTokensPricesUsdc'
import { useWeb3React } from '@web3-react/core'
import multicall from 'utils/multicall'
import { getCraftsmanV2Address } from 'utils/addressHelpers'
import { BIG_ZERO } from 'utils/bigNumber'
import { getBalanceAmount } from 'utils/formatBalance'
import { farmsConfig } from 'config/constants'
import { SerializedFarmConfig } from 'config/constants/types'
import craftsmanV2ABI from 'config/abi/craftsmanV2.json'
import { serializeTokensByAddress } from 'config/constants/tokens'
import { TOKEN_PRICE_MAPPING } from 'config/constants/tokenPriceMappings'

const tokenList = serializeTokensByAddress()
export interface FarmWithBalance extends SerializedFarmConfig {
  earningsV2: any[]
}

interface FarmsV2WithBalance {
  farmsV2WithStakedBalance: FarmWithBalance[]
  earningsUsdc: number
}

const initValue: FarmsV2WithBalance = {
  farmsV2WithStakedBalance: [],
  earningsUsdc: 0,
}

const useFarmsV2WithBalanceResource = createDependableResource<FarmsV2WithBalance>({
  initValue,
  getValue: async () => {
    const account = getAccount()
    if (!account) return initValue

    const prices = useFarmTokensPricesUsdc.getState().value

    const farmsV2 = farmsConfig.filter((farmConfig) => farmConfig.version === 2)
    const calls = farmsV2.map((farm) => ({
      address: getCraftsmanV2Address(),
      name: 'pendingTokens',
      params: [farm.pid, account],
    }))

    const rawEarnings = await multicall(craftsmanV2ABI, calls)
    const parsedEarnings = rawEarnings.map(([tokenAddrs, earnings]) => {
      return { tokens: tokenAddrs, earnings: earnings.map((earning) => new BigNumber(earning._hex)) }
    })
    const results = farmsV2.map((farm, index) => ({ ...farm, earningsV2: parsedEarnings[index] }))
    let totalEarned = BIG_ZERO
    const farmsWithEarnings = results.filter((farm) => {
      let amountGT0 = false
      farm.earningsV2.earnings.forEach((amount: BigNumber, index: number) => {
        if (amount.gt(0)) {
          amountGT0 = true
          const tokenAddr = farm.earningsV2.tokens[index]
          const mappedTokenAddr = TOKEN_PRICE_MAPPING[tokenAddr.toLowerCase()] ?? tokenAddr
          totalEarned = totalEarned.plus(
            getBalanceAmount(amount, tokenList[mappedTokenAddr]?.decimals).times(prices[mappedTokenAddr.toLowerCase()]),
          )
        }
      })
      return amountGT0
    })

    return {
      farmsV2WithStakedBalance: farmsWithEarnings,
      earningsUsdc: totalEarned.toNumber(),
    }
  },
  dependResources: [useFarmTokensPricesUsdc],
  onInit: ({ resetValue, refresh, getIsStarted }) => {
    onAccountInitOrChange(() => {
      if (!getIsStarted()) return
      resetValue()
      refresh()
    })
  },
  dbgName: 'FarmsV2WithBalance',
})

const usePeriodicRefreshFarmsV2WithBalance = createPeriodicRefresher(
  async () => {
    await useFarmsV2WithBalanceResource.getState().refresh()
  },
  {
    frequency: 'normal',
    dbgName: 'PollFarmsV2WithBalance',
  },
)

const useFarmsV2WithBalance = () => {
  const { account } = useWeb3React()
  usePeriodicRefreshFarmsV2WithBalance(Boolean(account))
  return useFarmsV2WithBalanceResource((s) => s.value)
}

export default useFarmsV2WithBalance
